import React, { useState, useEffect } from 'react';
import { login, setToken, getToken, onUnauthorized, apiJson } from './api';
import { colors, theme } from './styles';
import Dashboard from './components/Dashboard';
import Accounts from './components/Accounts';
import Settings from './components/Settings';
import Targets from './components/Targets';
import Whitelist from './components/Whitelist';
import Comments from './components/Comments';
import History from './components/History';
import Analytics from './components/Analytics';
import LiveView from './components/LiveView';

// --- Login ---
function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await login(username, password);
      onLogin();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%)',
    }}>
      <div style={{
        background: colors.bgCard, borderRadius: 12, padding: '48px 40px', width: 380,
        border: `1px solid ${colors.borderLight}`, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        textAlign: 'center',
      }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: '#fff', marginBottom: 4, letterSpacing: 2 }}>
          UnrealPS
        </h1>
        <p style={{ fontSize: 13, color: colors.textDim, marginBottom: 32 }}>Automation Control Panel</p>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input type="text" placeholder="Username" value={username}
            onChange={e => setUsername(e.target.value)} style={theme.input} autoFocus />
          <input type="password" placeholder="Password" value={password}
            onChange={e => setPassword(e.target.value)} style={theme.input} />
          {error && <div style={{ color: colors.red, fontSize: 13 }}>{error}</div>}
          <button type="submit" style={{ ...theme.btnPrimary, marginTop: 8, padding: 12 }}>Sign In</button>
        </form>
      </div>
    </div>
  );
}

// --- Navigation ---
const NAV_GLOBAL = [
  { key: 'dashboard', label: 'Dashboard', icon: 'D' },
  { key: 'accounts', label: 'Accounts', icon: 'A' },
  { key: 'live', label: 'Live View', icon: 'L' },
  { key: 'history', label: 'All History', icon: 'H' },
  { key: 'analytics', label: 'All Analytics', icon: 'G' },
];

const NAV_ACCOUNT = [
  { key: 'settings', label: 'Settings', icon: 'S' },
  { key: 'targets', label: 'Targets', icon: 'T' },
  { key: 'whitelist', label: 'Whitelist', icon: 'W' },
  { key: 'comments', label: 'Comments', icon: 'C' },
  { key: 'acc_live', label: 'Live View', icon: 'L' },
  { key: 'acc_history', label: 'History', icon: 'H' },
  { key: 'acc_analytics', label: 'Analytics', icon: 'G' },
];

// --- Dashboard ---
function AppShell({ onLogout }) {
  const [page, setPage] = useState('dashboard');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [statusSummary, setStatusSummary] = useState({ running: 0, total: 0, paused: 0 });

  useEffect(() => {
    const poll = async () => {
      const data = await apiJson('/status');
      if (data) {
        const s = data.statuses;
        setStatusSummary({
          total: s.length,
          running: s.filter(x => x.running).length,
          paused: s.filter(x => x.paused).length,
        });
      }
    };
    poll();
    const i = setInterval(poll, 5000);
    return () => clearInterval(i);
  }, []);

  const selectAccount = (acc) => {
    setSelectedAccount(acc);
    setPage('settings');
  };

  const refreshSelectedAccount = async () => {
    if (!selectedAccount) return;
    const data = await apiJson('/accounts');
    if (data) {
      const updated = data.accounts.find(a => a.id === selectedAccount.id);
      if (updated) setSelectedAccount(updated);
    }
  };

  const backToAccounts = () => {
    setSelectedAccount(null);
    setPage('accounts');
  };

  const renderPage = () => {
    switch (page) {
      case 'dashboard':
        return <Dashboard onSelectAccount={selectAccount} />;
      case 'accounts':
        return <Accounts onSelectAccount={selectAccount} />;
      case 'live':
        return <LiveView account={null} />;
      case 'history':
        return <History account={null} />;
      case 'analytics':
        return <Analytics account={null} />;
      case 'settings':
        return selectedAccount ? <Settings account={selectedAccount} onAccountUpdated={refreshSelectedAccount} /> : null;
      case 'targets':
        return selectedAccount ? <Targets account={selectedAccount} /> : null;
      case 'whitelist':
        return selectedAccount ? <Whitelist account={selectedAccount} /> : null;
      case 'comments':
        return selectedAccount ? <Comments account={selectedAccount} /> : null;
      case 'acc_live':
        return selectedAccount ? <LiveView account={selectedAccount} /> : null;
      case 'acc_history':
        return selectedAccount ? <History account={selectedAccount} /> : null;
      case 'acc_analytics':
        return selectedAccount ? <Analytics account={selectedAccount} /> : null;
      default:
        return <Accounts onSelectAccount={selectAccount} />;
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: colors.bg }}>
      {/* Sidebar */}
      <div style={{
        width: 200, background: colors.bgCard, borderRight: `1px solid ${colors.border}`,
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Logo */}
        <div style={{ padding: '16px', borderBottom: `1px solid ${colors.border}`, textAlign: 'center' }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: 0, letterSpacing: 2 }}>
            UnrealPS
          </h1>
          <div style={{ fontSize: 10, color: colors.textDim, marginTop: 4 }}>
            {statusSummary.running} running / {statusSummary.total} accounts
          </div>
        </div>

        {/* Global nav */}
        <div style={{ padding: '8px 0' }}>
          <div style={{ padding: '4px 12px', fontSize: 10, color: colors.textDimmer, textTransform: 'uppercase', letterSpacing: 1 }}>
            Global
          </div>
          {NAV_GLOBAL.map(item => (
            <NavItem
              key={item.key}
              item={item}
              active={page === item.key && !selectedAccount}
              onClick={() => { setSelectedAccount(null); setPage(item.key); }}
            />
          ))}
        </div>

        {/* Account nav */}
        {selectedAccount && (
          <div style={{ padding: '8px 0', borderTop: `1px solid ${colors.border}` }}>
            <div style={{ padding: '4px 12px', fontSize: 10, color: colors.textDimmer, textTransform: 'uppercase', letterSpacing: 1 }}>
              @{selectedAccount.username}
            </div>
            {NAV_ACCOUNT.map(item => (
              <NavItem
                key={item.key}
                item={item}
                active={page === item.key}
                onClick={() => setPage(item.key)}
              />
            ))}
            <div style={{ padding: '4px 8px' }}>
              <button
                style={{ ...theme.btnSecondary, width: '100%', fontSize: 11, padding: '6px' }}
                onClick={backToAccounts}
              >
                Back to Accounts
              </button>
            </div>
          </div>
        )}

        {/* Logout */}
        <div style={{ marginTop: 'auto', padding: 12, borderTop: `1px solid ${colors.border}` }}>
          <button style={{ ...theme.btnSecondary, width: '100%', fontSize: 11, padding: '6px' }} onClick={onLogout}>
            Logout
          </button>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {renderPage()}
      </div>
    </div>
  );
}

function NavItem({ item, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', padding: '8px 12px',
        background: active ? colors.accent + '22' : 'transparent',
        border: 'none', borderLeft: active ? `3px solid ${colors.accent}` : '3px solid transparent',
        color: active ? '#fff' : colors.textMuted,
        fontSize: 13, cursor: 'pointer', textAlign: 'left',
      }}
    >
      <span style={{
        width: 22, height: 22, borderRadius: 4,
        background: active ? colors.accent : colors.bgInput,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, color: active ? '#fff' : colors.textDim,
      }}>
        {item.icon}
      </span>
      {item.label}
    </button>
  );
}

// --- Root ---
export default function App() {
  const [authed, setAuthed] = useState(!!getToken());

  useEffect(() => {
    onUnauthorized(() => {
      setToken(null);
      setAuthed(false);
    });

    if (getToken()) {
      apiJson('/auth/me').then(data => {
        if (!data) { setToken(null); setAuthed(false); }
      });
    }
  }, []);

  if (!authed) return <LoginPage onLogin={() => setAuthed(true)} />;
  return <AppShell onLogout={() => { setToken(null); setAuthed(false); }} />;
}
